import type { VaultNote } from "./types.js";

/**
 * Turns the vault's flat note list into the folder tree it already describes.
 *
 * Every note carries its full path, so the hierarchy is derivable and nothing
 * new has to be stored or synced for it. What matters is when the derivation
 * runs. A migration of 857 notes across 18 areas makes a per-render walk of the
 * whole corpus visible as scroll jank, so the tree is built once per change to
 * the note list (`createFolderIndex`) and each render only flattens the rows a
 * reader can actually see (`treeRows` descends into expanded folders alone).
 *
 * Titles come from `note.title`, which `frontmatter.ts` derived by the one rule
 * the reader also uses. Deriving a name here by a second rule would put two
 * names for one note on a single screen.
 */

/** One folder and everything filed directly beneath it. */
export interface FolderNode {
  /** Slash-joined folder path. Empty for the vault root. */
  readonly path: string;
  /** The last segment, which is what a row shows. */
  readonly name: string;
  readonly folders: readonly FolderNode[];
  readonly notes: readonly VaultNote[];
  /** Notes at or below this folder, so a collapsed row can still say how many. */
  readonly total: number;
  /** The newest `updatedAt` at or below this folder; empty when there is none. */
  readonly latest: string;
}

/**
 * How notes are ordered inside each folder.
 *
 * `title` is the browsing order. `recency` is for the "Recently changed"
 * filter: notes sort newest-first inside their folder, and folders sort by their
 * newest descendant, so the freshest area rises to the top of the tree. The
 * ordering stays inside the tree rather than flattening across it because the
 * folder is what a reader uses to tell two same-titled notes apart; a flat
 * recency list already exists in the reader's activity feed.
 */
export type NoteOrder = "title" | "recency";

/** A row of the rendered tree. Only expanded branches produce any. */
export type TreeRow =
  | {
      readonly kind: "folder";
      readonly path: string;
      readonly name: string;
      readonly depth: number;
      readonly total: number;
      readonly expanded: boolean;
    }
  | {
      readonly kind: "note";
      readonly note: VaultNote;
      readonly depth: number;
      /**
       * Set only when another visible row carries the same title: the folder
       * (or, failing that, the path) that tells the two apart.
       */
      readonly hint?: string;
    };

interface Builder {
  readonly path: string;
  readonly name: string;
  readonly folders: Map<string, Builder>;
  readonly notes: VaultNote[];
}

function builder(path: string, name: string): Builder {
  return { path, name, folders: new Map(), notes: [] };
}

/** The folder segments of a note path, outermost first. Files have no folder. */
export function folderSegments(path: string): readonly string[] {
  return path.split("/").slice(0, -1).filter(Boolean);
}

/** Every folder a path sits inside, outermost first, as full folder paths. */
export function ancestorFolders(path: string): readonly string[] {
  const trail: string[] = [];
  for (const segment of folderSegments(path)) {
    trail.push(trail.length === 0 ? segment : `${trail[trail.length - 1]}/${segment}`);
  }
  return trail;
}

function byTitle(a: VaultNote, b: VaultNote): number {
  return a.title.localeCompare(b.title) || a.path.localeCompare(b.path);
}

function byRecency(a: VaultNote, b: VaultNote): number {
  return b.updatedAt.localeCompare(a.updatedAt) || byTitle(a, b);
}

function freeze(node: Builder, order: NoteOrder): FolderNode {
  const folders = [...node.folders.values()]
    .map((child) => freeze(child, order))
    .sort((a, b) => (order === "recency" ? b.latest.localeCompare(a.latest) : 0) || a.name.localeCompare(b.name));
  const notes = [...node.notes].sort(order === "recency" ? byRecency : byTitle);
  const latest = [...notes.map(({ updatedAt }) => updatedAt), ...folders.map(({ latest: at }) => at)]
    .reduce((newest, at) => (at > newest ? at : newest), "");
  return {
    path: node.path,
    name: node.name,
    folders,
    notes,
    total: notes.length + folders.reduce((sum, folder) => sum + folder.total, 0),
    latest,
  };
}

export function buildFolderTree(notes: readonly VaultNote[], order: NoteOrder = "title"): FolderNode {
  const root = builder("", "");
  for (const note of notes) {
    let current = root;
    for (const segment of folderSegments(note.path)) {
      const path = current.path === "" ? segment : `${current.path}/${segment}`;
      let next = current.folders.get(segment);
      if (!next) {
        next = builder(path, segment);
        current.folders.set(segment, next);
      }
      current = next;
    }
    current.notes.push(note);
  }
  return freeze(root, order);
}

/**
 * A one-entry memo over `buildFolderTree`.
 *
 * The caller passes a key naming what produced this list — the active filter,
 * say — because two filters can hand over lists of equal length whose contents
 * differ. Rebuilding only when that key or the list itself changes is what
 * keeps a keystroke or a selection change from re-walking the whole vault.
 *
 * The list is compared by identity, not by contents. Callers must not mutate
 * `notes` in place: a push or splice into the same array is invisible here and
 * the stale tree would be served until the array itself is replaced. Every
 * change to the note list has to be a new array.
 */
export function createFolderIndex(): (key: string, notes: readonly VaultNote[], order?: NoteOrder) => FolderNode {
  let cachedKey: string | undefined;
  let cachedNotes: readonly VaultNote[] | undefined;
  let cachedOrder: NoteOrder | undefined;
  let cachedTree: FolderNode | undefined;
  return (key, notes, order = "title") => {
    if (cachedTree !== undefined && cachedKey === key && cachedNotes === notes && cachedOrder === order) return cachedTree;
    cachedKey = key;
    cachedNotes = notes;
    cachedOrder = order;
    cachedTree = buildFolderTree(notes, order);
    return cachedTree;
  };
}

/**
 * Tells apart the visible notes that share a title.
 *
 * Two `readme.md` files in two open projects would otherwise be two identical
 * rows. The hint is the parent folder when that note is the only one with its
 * title in that folder, and the full path when it is not — decided per folder,
 * so duplicates sharing a folder are still told apart when a third copy
 * elsewhere makes the title's parents look distinct. Notes whose title is
 * unique among what is on screen get no hint, so the common case stays quiet.
 */
function disambiguate(rows: readonly TreeRow[]): readonly TreeRow[] {
  const byTitle = new Map<string, number>();
  for (const row of rows) {
    if (row.kind === "note") byTitle.set(row.note.title, (byTitle.get(row.note.title) ?? 0) + 1);
  }
  if (![...byTitle.values()].some((count) => count > 1)) return rows;
  // Keyed by title and parent together: the decision is per group of notes
  // sharing a folder, not per title. Three same-titled notes across two
  // folders means the two that share a folder still need their full paths,
  // even though the title as a whole has more than one parent.
  const byTitleAndParent = new Map<string, number>();
  const group = (title: string, parent: string): string => `${title}\u0000${parent}`;
  for (const row of rows) {
    if (row.kind !== "note" || (byTitle.get(row.note.title) ?? 0) < 2) continue;
    const key = group(row.note.title, parentFolder(row.note.path));
    byTitleAndParent.set(key, (byTitleAndParent.get(key) ?? 0) + 1);
  }
  return rows.map((row) => {
    if (row.kind !== "note" || (byTitle.get(row.note.title) ?? 0) < 2) return row;
    const parent = parentFolder(row.note.path);
    const alone = (byTitleAndParent.get(group(row.note.title, parent)) ?? 0) === 1;
    const hint = alone ? (parent || "vault root") : row.note.path;
    return { ...row, hint };
  });
}

/**
 * Flattens the tree into the rows to render, descending only where a folder is
 * expanded. A collapsed area of 200 notes costs one row, not 200.
 */
export function treeRows(root: FolderNode, expanded: ReadonlySet<string>): readonly TreeRow[] {
  const rows: TreeRow[] = [];
  const walk = (node: FolderNode, depth: number): void => {
    for (const folder of node.folders) {
      const open = expanded.has(folder.path);
      rows.push({ kind: "folder", path: folder.path, name: folder.name, depth, total: folder.total, expanded: open });
      if (open) walk(folder, depth + 1);
    }
    for (const note of node.notes) rows.push({ kind: "note", note, depth });
  };
  walk(root, 0);
  return disambiguate(rows);
}

/** The row key used for focus and selection: a folder path or a note id. */
export function rowKey(row: TreeRow): string {
  return row.kind === "folder" ? `folder:${row.path}` : `note:${row.note.id}`;
}

/* ------------------------------------------------------------ semantics */

/**
 * The tree's accessibility contract lives here, beside the rows it describes,
 * so the markup and the keyboard model cannot drift apart. `main.ts` supplies
 * only the visible content of each row.
 *
 * The rendered tree is nested — every expanded folder owns a `role="group"`
 * holding its children — because that is the structure a screen reader
 * announces as "level 2, 3 of 5". One tab stop enters the tree; the arrow keys
 * move inside it.
 */

export interface TreeRenderOptions {
  /** The row that owns the keyboard: the tree's single tab stop. */
  readonly focusKey: string;
  readonly selectedId: string;
  readonly folderContent: (row: Extract<TreeRow, { kind: "folder" }>) => string;
  readonly noteContent: (row: Extract<TreeRow, { kind: "note" }>) => string;
}

function attribute(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;");
}

/** The ARIA attributes, roving tab stop, and data hooks of one tree item. */
export function treeItemAttributes(row: TreeRow, focusKey: string, selectedId: string): string {
  const key = rowKey(row);
  const shared = `role="treeitem" aria-level="${row.depth + 1}" tabindex="${key === focusKey ? 0 : -1}" data-row="${attribute(key)}"`;
  if (row.kind === "folder") {
    return `${shared} aria-expanded="${row.expanded}" data-parent="${attribute(parentFolder(row.path))}" data-folder="${attribute(row.path)}"`;
  }
  const selected = row.note.id === selectedId;
  return `${shared} aria-selected="${selected}" data-parent="${attribute(parentFolder(row.note.path))}" data-note="${attribute(row.note.id)}"`;
}

/** The parent folder of a path, as the tree's own key. Empty at the root. */
export function parentFolder(path: string): string {
  return ancestorFolders(path).at(-1) ?? "";
}

/** Renders the tree as nested tree items, descending only into open folders. */
export function renderTree(root: FolderNode, expanded: ReadonlySet<string>, options: TreeRenderOptions): string {
  const hints = new Map<string, string>();
  for (const row of treeRows(root, expanded)) {
    if (row.kind === "note" && row.hint !== undefined) hints.set(row.note.id, row.hint);
  }
  const walk = (node: FolderNode, depth: number): string => {
    const folders = node.folders.map((folder) => {
      const open = expanded.has(folder.path);
      const row: TreeRow = { kind: "folder", path: folder.path, name: folder.name, depth, total: folder.total, expanded: open };
      const children = open ? `<div role="group">${walk(folder, depth + 1)}</div>` : "";
      return `<div class="tree-item" ${treeItemAttributes(row, options.focusKey, options.selectedId)}>${options.folderContent(row)}${children}</div>`;
    });
    const notes = node.notes.map((note) => {
      const hint = hints.get(note.id);
      const row: TreeRow = hint === undefined ? { kind: "note", note, depth } : { kind: "note", note, depth, hint };
      return `<div class="tree-item" ${treeItemAttributes(row, options.focusKey, options.selectedId)}>${options.noteContent(row)}</div>`;
    });
    return [...folders, ...notes].join("");
  };
  return walk(root, 0);
}

/** What a key pressed on a tree row asks the tree to do. */
export type TreeKeyIntent =
  | { readonly kind: "focus"; readonly key: string }
  | { readonly kind: "expand"; readonly path: string }
  | { readonly kind: "collapse"; readonly path: string }
  | { readonly kind: "activate"; readonly row: TreeRow };

/**
 * The WAI-ARIA tree keyboard model over the visible rows: Up/Down step,
 * Home/End jump, Right opens a folder or steps into it, Left closes a folder
 * or climbs to the parent, Enter and Space activate the row.
 */
export function treeKeyIntent(key: string, rows: readonly TreeRow[], index: number): TreeKeyIntent | undefined {
  const current = rows[index];
  if (!current) return undefined;
  const focusAt = (to: number): TreeKeyIntent => ({ kind: "focus", key: rowKey(rows[Math.min(Math.max(to, 0), rows.length - 1)]!) });
  switch (key) {
    case "ArrowDown": return focusAt(index + 1);
    case "ArrowUp": return focusAt(index - 1);
    case "Home": return focusAt(0);
    case "End": return focusAt(rows.length - 1);
    case "Enter":
    case " ": return { kind: "activate", row: current };
    case "ArrowRight":
      if (current.kind === "folder" && !current.expanded) return { kind: "expand", path: current.path };
      return index + 1 < rows.length ? focusAt(index + 1) : undefined;
    case "ArrowLeft": {
      if (current.kind === "folder" && current.expanded) return { kind: "collapse", path: current.path };
      const parent = parentFolder(current.kind === "folder" ? current.path : current.note.path);
      return parent ? { kind: "focus", key: `folder:${parent}` } : undefined;
    }
    default: return undefined;
  }
}
